import { useEffect, useState } from 'react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { SendOfferModal, type OfferData } from '@/components/athletes/SendOfferModal'
import { offerRequestBody } from '@/components/athletes/offerRequest'
import type { Athlete } from '@/components/athletes/AthleteCard'
import { useCreateCollaborationMutation, useGetCampaignsQuery } from '@/redux/marketerApi'

// A brand makes an offer to the athlete they're messaging. Every deal belongs to a campaign:
// the brand can pick one, or leave it to the server to create one just for this deal.

interface ThreadOfferDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  threadId: string
  athlete: { id: string; name: string; avatar: string | null }
  /** Shown under the athlete's name, e.g. the talent sheet the conversation came from */
  subtitle?: string | null
  /** Called with the new deal's ID once the offer is sent */
  onSent: (collaborationId: string) => void
}

export const ThreadOfferDialog = ({
  open,
  onOpenChange,
  threadId,
  athlete,
  subtitle,
  onSent,
}: ThreadOfferDialogProps) => {
  const { toast } = useToast()
  const { data: campaigns = [] } = useGetCampaignsQuery(undefined, { skip: !open })
  const [createCollaboration, { isLoading }] = useCreateCollaborationMutation()
  // Every deal belongs to a campaign: the offer goes into one the brand picks
  const activeCampaigns = campaigns.filter((c) => c.status !== 'completed')
  const [campaignId, setCampaignId] = useState('')

  useEffect(() => {
    if (open) setCampaignId(activeCampaigns[0]?.id ?? '')
  }, [open, activeCampaigns.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // SendOfferModal takes a full athlete; a conversation only carries these fields
  const offerAthlete = {
    id: athlete.id,
    name: athlete.name,
    avatar: athlete.avatar,
    school: null,
    sports: [],
  } as unknown as Athlete

  const handleSubmit = async (offerData: OfferData) => {
    if (!campaignId) {
      toast({
        title: 'Pick a campaign',
        description: 'Bookings go into one of your campaigns. Create one first if you need to.',
        variant: 'destructive',
      })
      return
    }
    try {
      const response = await createCollaboration({
        athleteId: athlete.id,
        threadId,
        campaignId,
        ...offerRequestBody(offerData),
      }).unwrap()
      toast({ title: 'Booking sent', description: `${athlete.name} can now review your booking.` })
      onOpenChange(false)
      onSent(response.id)
    } catch (err: any) {
      toast({
        title: 'Could not send the booking',
        description: err?.data?.error || err?.message || 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const campaignPicker =
    activeCampaigns.length === 0 ? (
      <p className="rounded-lg border border-foreground/10 px-3 py-2 text-sm text-gray-600">
        Bookings go into a campaign.{' '}
        <a href="/campaign/create" className="font-semibold text-[#3F7A0A] underline">
          Create a campaign
        </a>{' '}
        first, then book.
      </p>
    ) : (
      <div className="space-y-1.5">
        <Label className="text-gray-700 text-xs uppercase tracking-wider">Campaign</Label>
        <Select value={campaignId} onValueChange={setCampaignId}>
          <SelectTrigger className="border-foreground/15 bg-card text-foreground">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {activeCampaigns.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )

  return (
    <SendOfferModal
      open={open}
      onOpenChange={onOpenChange}
      athlete={offerAthlete}
      onSubmit={handleSubmit}
      isSending={isLoading}
      beforeMessage={campaignPicker}
      offerOnly
      title="Book"
      athleteSubtitle={subtitle ?? null}
    />
  )
}
